// The realbook.site corpus as a tune SOURCE — the openbook pattern, applied
// to ~850 MusicXML lead sheets fetched by scripts/fetch-realbook.mjs and
// validated by scripts/validate-realbook.ts. The manifest lists ONLY tunes
// the app's own importer parses (the validator ran every file through
// parseMusicXMLScore), so a row in the palette is a tune that loads.
//
// Payloads stay in public/realbook/ and fetch on demand: 850 scores embedded
// in catalogue documents would put ~40MB of JSON in the eager bundle glob.
// Rights: every file is an uncleared transcription of a copyrighted
// composition; the corpus is counted by scripts/check-release.mjs, so the
// public build fails closed on it exactly as it does on the drum audio.

import { parseMusicXMLScore } from '@/music/musicxml/parseScore';
import type { Score } from '@/music/score/types';

export interface RealbookLibraryEntry {
  id: string;
  file: string;
  title: string;
  composer?: string;
  /** Bars in the form, for the picker subtitle. */
  bars?: number;
  key?: string;
  /** Carries sung words. Written by the validator only when true, so absent
   * means "no lyrics" — the browser badges rows without loading 850 scores. */
  lyrics?: boolean;
}

let manifestPromise: Promise<RealbookLibraryEntry[]> | null = null;

/** Every validated realbook tune, sorted by title. Resolves to [] when the
 *  manifest can't be fetched (offline dev, tests without a server). */
export function realbookLibrary(): Promise<RealbookLibraryEntry[]> {
  manifestPromise ??= (async () => {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}realbook/manifest.json`);
      if (!res.ok) return [];
      const list: unknown = await res.json();
      if (!Array.isArray(list)) return [];
      return (list as RealbookLibraryEntry[]).sort((a, b) => a.title.localeCompare(b.title));
    } catch {
      return [];
    }
  })();
  return manifestPromise;
}

/** Load one realbook tune as a Score. */
export async function loadRealbookScore(entry: { id: string; file: string }): Promise<Score> {
  const res = await fetch(`${import.meta.env.BASE_URL}realbook/${entry.file}`);
  if (!res.ok) throw new Error(`realbook fetch failed: ${entry.file}`);
  const xml = await res.text();
  const score = parseMusicXMLScore(xml);
  // realbook's composer lines carry a year prefix ("1959 - John Coltrane");
  // the year belongs to metadata, not the byline.
  const composer = score.composer?.replace(/^\s*\d{4}\s*[-–]\s*/, '').trim() || score.composer;
  // Stamp a stable identity: band settings are remembered per tune under
  // Score.id, and a parsed score would otherwise arrive anonymous.
  return { ...score, composer, id: `realbook-${entry.id}` };
}

/** Test seam: forget the cached manifest. */
export function resetRealbookLibrary(): void {
  manifestPromise = null;
}
